// proceduralLibrary/toWallpaperCss.js
//
// Turn an SVG design (id + palette) into a CSS `background` value that fills the
// wallpaper surface (cover, centered) — stored in the existing `bg_wallpaper`
// slot via the same onApply(cssString) -> setBgGradient path the pattern panel
// and gradient presets use (currentBg === 3). Also parses such a value back into
// { id, paletteName } so the editor can re-open pre-filled.

import {
  generateDesignSvg,
  DESIGN_BY_ID,
  PALETTE_BY_NAME,
} from "./designs/designGenerators";

const encodeSvg = (svg) =>
  svg
    .replace(/\n/g, "")
    .replace(/"/g, "'")
    .replace(/%/g, "%25")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E")
    .replace(/&/g, "%26")
    .replace(/{/g, "%7B")
    .replace(/}/g, "%7D");

// Build the CSS `background` value for a design. Cover + centered so the design
// fills the surface at any aspect; a solid accent underneath as fallback.
// `paletteNameOrTriple` accepts either a curated palette NAME (string, resolved
// via PALETTE_BY_NAME) or an already-derived {light,deep,accent} triple object
// (a Segment/Wheel match-pill recolor) — either way it's forwarded to
// generateDesignSvg so the actual applied palette gets baked into the marker.
export const buildDesignCss = (id, paletteNameOrTriple) => {
  const entry = DESIGN_BY_ID[id];
  if (!entry) return "";
  const pal =
    (typeof paletteNameOrTriple === "string" &&
      PALETTE_BY_NAME[paletteNameOrTriple]) ||
    (paletteNameOrTriple && typeof paletteNameOrTriple === "object"
      ? paletteNameOrTriple
      : null) ||
    PALETTE_BY_NAME[entry.palette];
  const svg = generateDesignSvg(id, pal);
  if (!svg) return "";
  const url = `url("data:image/svg+xml,${encodeSvg(svg)}")`;
  const fallback = (pal && pal.accent) || "#ffffff";
  return `${url} center/cover no-repeat, ${fallback}`;
};

// True when a stored bg_wallpaper value is one of our generated designs.
export const isDesignCss = (value) =>
  typeof value === "string" &&
  value.includes("data:image/svg+xml") &&
  value.includes("data-iyuga-design");

// Reverse encodeSvg's '#' -> '%23' escaping inside the captured marker text
// (the marker never contains any of encodeSvg's other escaped characters).
const decodeMarker = (raw) => (raw ? raw.replace(/%23/g, "#") : raw);

// Recover { id, paletteName, palette? } from a stored design CSS string, or
// null. Reads the `data-iyuga-palette` marker baked by generateDesignSvg so
// this returns the palette the user ACTUALLY applied (a curated name, or the
// exact custom triple from a Segment/Wheel match-pill recolor) instead of
// always falling back to the design's hardcoded default palette.
export const parseDesignCss = (value) => {
  if (!isDesignCss(value)) return null;
  const id = (value.match(/data-iyuga-design=['"]([a-zA-Z]+)['"]/) || [])[1];
  if (!id || !DESIGN_BY_ID[id]) return null;
  const marker = decodeMarker(
    (value.match(/data-iyuga-palette=['"]([^'"]*)['"]/) || [])[1]
  );
  if (marker && marker.startsWith("custom:")) {
    const [light, deep, accent] = marker.slice("custom:".length).split("|");
    if (light && deep && accent) {
      return { id, paletteName: null, palette: { light, deep, accent } };
    }
  }
  const paletteName =
    marker && PALETTE_BY_NAME[marker] ? marker : DESIGN_BY_ID[id].palette;
  return { id, paletteName };
};
